import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { useUserStats } from '../context/UserStatsContext';
import { useSubscription } from '../context/SubscriptionContext';
import { Button } from '../components/common';
import { PLAN_NAMES, Complexity } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const { profile, setComplexity } = useUser();
  const { stats } = useUserStats();
  const { subscription, plan, isActive, createStripePortal } = useSubscription();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [isChangingComplexity, setIsChangingComplexity] = useState(false);

  const formatDate = (date: Date | null | undefined): string => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleManageSubscription = async () => {
    if (!subscription?.stripeCustomerId) {
      navigate('/pricing');
      return;
    }

    setIsOpeningPortal(true);
    try {
      const portalUrl = await createStripePortal(window.location.href);
      window.location.href = portalUrl;
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      setIsOpeningPortal(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE' || !currentUser) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${currentUser.uid}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete account');
      }

      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to delete account:', error);
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  const handleComplexityChange = async (newComplexity: Complexity) => {
    setIsChangingComplexity(true);
    try {
      await setComplexity(newComplexity);
    } catch (error) {
      console.error('Failed to change complexity:', error);
    } finally {
      setIsChangingComplexity(false);
    }
  };

  const getPlanBadgeColor = (planType: string): string => {
    switch (planType) {
      case 'kids':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string | undefined): string => {
    switch (status) {
      case 'active':
      case 'trialing':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-yellow-100 text-yellow-800';
      case 'past_due':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const totalSessions = stats
    ? stats.grammarSessions + stats.readingSessions + stats.listeningSessions + stats.speakingSessions
    : 0;

  return (
    <div className="min-h-screen bg-kids-bg dark:bg-slate-900">
      {/* Header */}
      <header className="bg-white dark:bg-slate-800 shadow-sm dark:shadow-slate-900">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/app')}
            className="flex items-center text-gray-600 dark:text-gray-300 hover:text-kids-primary dark:hover:text-indigo-400 transition-colors"
          >
            <span className="text-2xl mr-2">{'\u2190'}</span>
            Back to App
          </button>
          <h1 className="text-xl font-bold text-gray-800 dark:text-white">My Profile</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* User Info Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm dark:shadow-slate-900 p-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span className="text-2xl mr-2">{'\u{1F464}'}</span>
            Account Information
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Email</span>
              <span className="text-gray-800 dark:text-white font-medium">{currentUser?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Age</span>
              <span className="text-gray-800 dark:text-white">{profile?.age || 'Not set'}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Member since</span>
              <span className="text-gray-800 dark:text-white">{formatDate(stats?.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Difficulty Level Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm dark:shadow-slate-900 p-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span className="text-2xl mr-2">{'\u{1F3AF}'}</span>
            Difficulty Level
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Choose the difficulty that's right for you. This affects all exercises.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(['basic', 'intermediate', 'advanced'] as Complexity[]).map((level) => (
              <button
                key={level}
                onClick={() => handleComplexityChange(level)}
                disabled={isChangingComplexity}
                className={`p-4 rounded-2xl border-2 transition-all ${
                  profile?.complexity === level
                    ? 'border-kids-primary bg-indigo-50 dark:bg-indigo-900/30'
                    : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                } ${isChangingComplexity ? 'opacity-50' : ''}`}
              >
                <div className="text-3xl mb-2">
                  {level === 'basic' ? '\u{1F476}' : level === 'intermediate' ? '\u{1F9D2}' : '\u{1F393}'}
                </div>
                <div className="font-bold text-gray-800 dark:text-white">
                  {level === 'basic' ? 'Easy' : level === 'intermediate' ? 'Medium' : 'Hard'}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {level === 'basic' ? 'Ages 4-6' : level === 'intermediate' ? 'Ages 7-9' : 'Ages 10-12'}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Subscription Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm dark:shadow-slate-900 p-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span className="text-2xl mr-2">{'\u{1F4B3}'}</span>
            Subscription
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Current Plan</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPlanBadgeColor(plan)}`}>
                {PLAN_NAMES[plan]}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Status</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeColor(subscription?.status)}`}>
                {subscription?.status ? subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1) : 'Active'}
              </span>
            </div>
            {subscription?.billingCycle && plan !== 'free' && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
                <span className="text-gray-600 dark:text-gray-400">Billing Cycle</span>
                <span className="text-gray-800 dark:text-white">
                  {subscription.billingCycle === 'annual' ? 'Annual' : 'Monthly'}
                </span>
              </div>
            )}
            {subscription?.currentPeriodEnd && plan !== 'free' && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
                <span className="text-gray-600 dark:text-gray-400">
                  {subscription?.cancelAtPeriodEnd ? 'Access until' : 'Next billing date'}
                </span>
                <span className="text-gray-800 dark:text-white">{formatDate(subscription.currentPeriodEnd)}</span>
              </div>
            )}
            {subscription?.cancelAtPeriodEnd && (
              <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-xl p-3 mt-2">
                <p className="text-yellow-800 dark:text-yellow-400 text-sm">
                  Your subscription will be canceled at the end of the current billing period.
                </p>
              </div>
            )}
          </div>
          <div className="mt-6 flex gap-3">
            {plan === 'free' ? (
              <Button
                onClick={() => navigate('/pricing')}
                variant="primary"
                fullWidth
              >
                Upgrade to Premium
              </Button>
            ) : (
              <Button
                onClick={handleManageSubscription}
                disabled={isOpeningPortal}
                variant="primary"
                fullWidth
              >
                {isOpeningPortal ? 'Opening...' : 'Manage Subscription'}
              </Button>
            )}
          </div>
        </div>

        {/* Practice Stats Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm dark:shadow-slate-900 p-6">
          <h2 className="text-lg font-bold text-gray-800 dark:text-white mb-4 flex items-center">
            <span className="text-2xl mr-2">{'\u{1F4CA}'}</span>
            Practice Statistics
          </h2>

          {/* Main Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-indigo-50 dark:bg-indigo-900/30 rounded-2xl p-4 text-center">
              <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                {formatTime(stats?.totalPracticeMinutes || 0)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Total Practice</p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-2xl p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{totalSessions}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Total Sessions</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/30 rounded-2xl p-4 text-center">
              <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">{stats?.currentStreak || 0}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Day Streak</p>
            </div>
            <div className="bg-pink-50 dark:bg-pink-900/30 rounded-2xl p-4 text-center">
              <p className="text-3xl font-bold text-pink-600 dark:text-pink-400">{stats?.longestStreak || 0}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">Best Streak</p>
            </div>
          </div>

          {/* Section Stats */}
          <h3 className="font-bold text-gray-700 dark:text-gray-200 mb-3">Sessions by Section</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-3 text-center">
              <span className="text-2xl">{'\u{1F4DD}'}</span>
              <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{stats?.grammarSessions || 0}</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">Grammar</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-3 text-center">
              <span className="text-2xl">{'\u{1F4DA}'}</span>
              <p className="text-xl font-bold text-green-600 dark:text-green-400">{stats?.readingSessions || 0}</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">Reading</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/30 rounded-xl p-3 text-center">
              <span className="text-2xl">{'\u{1F3A7}'}</span>
              <p className="text-xl font-bold text-orange-600 dark:text-orange-400">{stats?.listeningSessions || 0}</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">Listening</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-3 text-center">
              <span className="text-2xl">{'\u{1F399}'}</span>
              <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{stats?.speakingSessions || 0}</p>
              <p className="text-xs text-gray-600 dark:text-gray-300">Speaking</p>
            </div>
          </div>

          {/* Average Scores */}
          <h3 className="font-bold text-gray-700 dark:text-gray-200 mb-3">Average Scores</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Grammar</span>
              <span className="text-gray-800 dark:text-white font-medium">
                {stats?.averageGrammarScore ? `${Math.round(stats.averageGrammarScore)}%` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Reading</span>
              <span className="text-gray-800 dark:text-white font-medium">
                {stats?.averageReadingScore ? `${Math.round(stats.averageReadingScore)}%` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Listening</span>
              <span className="text-gray-800 dark:text-white font-medium">
                {stats?.averageListeningScore ? `${Math.round(stats.averageListeningScore)}%` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
              <span className="text-gray-600 dark:text-gray-400">Speaking</span>
              <span className="text-gray-800 dark:text-white font-medium">
                {stats?.averageSpeakingScore ? `${Math.round(stats.averageSpeakingScore)}%` : 'N/A'}
              </span>
            </div>
          </div>

          {/* Last Practice */}
          <div className="mt-4 flex justify-between items-center py-2 border-b border-gray-100 dark:border-slate-700">
            <span className="text-gray-600 dark:text-gray-400">Last Practice</span>
            <span className="text-gray-800 dark:text-white">{formatDate(stats?.lastPracticeDate)}</span>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm dark:shadow-slate-900 p-6 border-2 border-red-200 dark:border-red-800">
          <h2 className="text-lg font-bold text-red-700 dark:text-red-400 mb-4 flex items-center">
            <span className="text-2xl mr-2">{'\u26A0\uFE0F'}</span>
            Danger Zone
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Once you delete your account, there is no going back. All your data will be permanently removed.
          </p>
          <Button
            onClick={() => setShowDeleteModal(true)}
            variant="secondary"
            className="!bg-red-600 !text-white hover:!bg-red-700"
          >
            Delete Account
          </Button>
        </div>
      </main>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl dark:shadow-slate-900 max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">Delete Account</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              This action cannot be undone. This will permanently delete your account and remove all your data from our servers.
            </p>
            {subscription && plan !== 'free' && isActive && (
              <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-xl p-3 mb-4">
                <p className="text-yellow-800 dark:text-yellow-400 text-sm">
                  Your active subscription will be canceled immediately and you will not be refunded.
                </p>
              </div>
            )}
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              Please type <span className="font-mono font-bold">DELETE</span> to confirm:
            </p>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="w-full border-2 border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-white rounded-xl px-4 py-3 mb-4 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
            {deleteError && (
              <p className="text-red-600 dark:text-red-400 text-sm mb-4">{deleteError}</p>
            )}
            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                  setDeleteError(null);
                }}
                variant="secondary"
                fullWidth
              >
                Cancel
              </Button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                className="flex-1 bg-red-600 text-white py-3 px-4 rounded-xl font-bold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
